import Ajv2020, { type ValidateFunction } from 'ajv/dist/2020';
import addFormats from 'ajv-formats';

import {
  APPOINTMENT_BROWSER_CONTRACT,
  EXACT_APPOINTMENT_CONTRACT,
  NATIVE_COACH_RESCHEDULE_CONTRACT,
} from './browserCapability';
import {
  parseAppointmentTimestamp as parseTimestamp,
  validateBrowserAppointmentPage,
  validateWireSemantics as validateCapabilityNeutralWireSemantics,
  validateMutationResult,
  validateOperationStatus,
} from './browserContractSemantics';

import type { ParticipantAppointmentErrorEnvelope } from './participantAppointmentError';
// Keeping the catalog's paired wire/domain types qualified makes each codec auditable.
// eslint-disable-next-line import/no-namespace -- Qualified names expose each codec's exact pair.
import type * as AppointmentTypes from './types';

const ajv = new Ajv2020({ allErrors: true, strict: false });
addFormats(ajv, ['date', 'date-time', 'uuid']);
const schemas = import.meta.glob<Record<string, unknown>>(
  './contracts/*/schemas/*.schema.json',
  { eager: true, import: 'default' },
);
Object.values(schemas).forEach((schema) => ajv.addSchema(schema));

const artifactTexts = import.meta.glob<string>(
  ['./contracts/v1/fixtures/*.json', './contracts/v1/schemas/*.schema.json'],
  { eager: true, import: 'default', query: '?raw' },
);
const CANONICAL_MANIFEST_SHA256 =
  '73585b502c8f1f8e60486c2bd8d4e36ef0a1203b7875e99981e39e86496de15f';
const [artifactManifestText] = Object.values(
  import.meta.glob<string>('./contracts/v1/manifest.json', {
    eager: true,
    import: 'default',
    query: '?raw',
  }),
);

const compile = (id: string) => ajv.getSchema(`${id}.schema.json`)!;

type SemanticValidator<TDomain> = (value: TDomain) => void;

function failUnless(condition: boolean, message: string): asserts condition {
  if (!condition) {
    throw new Error(message);
  }
}

const toCamelCase = (key: string) =>
  key.replace(/_([a-z])/gu, (_, character: string) => character.toUpperCase());

const toSnakeCase = (key: string) =>
  key.replace(/[A-Z]/gu, (character) => `_${character.toLowerCase()}`);

const mapKeys = (value: unknown, mapKey: (key: string) => string): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => mapKeys(item, mapKey));
  }
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        mapKey(key),
        mapKeys(item, mapKey),
      ]),
    );
  }
  return value;
};

const validateSeriesFenceWireSemantics = (value: unknown): void => {
  if (Array.isArray(value)) {
    value.forEach(validateSeriesFenceWireSemantics);
    return;
  }
  if (value === null || typeof value !== 'object') return;

  const object = value as Record<string, unknown>;
  if (
    object.contract_version === 'appointments.v1' &&
    'series_id' in object &&
    'series_mutation_version' in object
  ) {
    const seriesId = object.series_id;
    const seriesMutationVersion = object.series_mutation_version;
    failUnless(
      (seriesId === null && seriesMutationVersion === 0) ||
        (typeof seriesId === 'string' &&
          typeof seriesMutationVersion === 'number' &&
          seriesMutationVersion > 0),
      'Appointment series mutation version must match Series membership',
    );
  }
  Object.values(object).forEach(validateSeriesFenceWireSemantics);
};

const validateEligibility = (
  eligibility: AppointmentTypes.SchedulingEligibility,
): void => {
  failUnless(
    eligibility.eligible
      ? eligibility.reasons.length === 0
      : eligibility.reasons.length > 0,
    eligibility.eligible
      ? 'eligible context requires no reasons'
      : 'ineligible context requires a reason',
  );
};

const validateScopes = (scopes: AppointmentTypes.AppointmentScope[]): void => {
  const canonical: AppointmentTypes.AppointmentScope[] = [
    'appointment',
    'this_and_following',
  ];
  let previousPosition = -1;
  scopes.forEach((scope) => {
    const position = canonical.indexOf(scope);
    failUnless(
      position > previousPosition,
      'scopes must be unique and canonically ordered',
    );
    previousPosition = position;
  });
};

const validateAvailability = (
  context:
    | AppointmentTypes.InternalSchedulingContext
    | AppointmentTypes.BrowserSchedulingContext
    | AppointmentTypes.AdminSchedulingContext
    | AppointmentTypes.ParticipantSchedulingContext,
): void => {
  context.availability.days.forEach((day, index, days) => {
    failUnless(
      index === 0 || day.date > days[index - 1]!.date,
      'availability days must be uniquely ordered by date',
    );
    failUnless(
      day.enabled || day.slots.length === 0,
      'disabled availability day must have no slots',
    );
    failUnless(
      context.kind !== 'chemistry' || day.appointmentCount === null,
      'chemistry day counts must be null',
    );
    day.slots.forEach((slot, slotIndex, slots) => {
      failUnless(
        slotIndex === 0 ||
          parseTimestamp(slot.startAt) >
            parseTimestamp(slots[slotIndex - 1]!.startAt),
        'availability slots must be uniquely ordered by start',
      );
    });
    failUnless(
      context.appointmentCount === null ||
        !day.enabled ||
        day.appointmentCount === context.appointmentCount,
      'top-level appointment count must match every enabled day',
    );
  });
};

type SchedulingContext =
  | AppointmentTypes.InternalSchedulingContext
  | AppointmentTypes.BrowserSchedulingContext
  | AppointmentTypes.AdminSchedulingContext
  | AppointmentTypes.ParticipantSchedulingContext;

const validateSchedulingKindAndIntent = (context: SchedulingContext): void => {
  failUnless(
    context.kind !== 'chemistry' ||
      (context.seatType === null &&
        context.appointmentCount === null &&
        context.cadenceWeeks === null &&
        context.activeSeries === null),
    'chemistry context requires null seat, recurrence, and active Series',
  );
  // MEN-5306: mirrors the backend's standaloneReschedule rule. Rescheduling a
  // target without Series membership has no cadence; recurring contexts and
  // bookings still require one.
  const standaloneReschedule =
    context.intent === 'reschedule' &&
    context.rescheduleTarget !== null &&
    context.rescheduleTarget.seriesId === null;
  failUnless(
    context.kind !== 'coaching' ||
      ('bookingPattern' in context
        ? context.appointmentCount === null && context.cadenceWeeks === null
        : context.seatType !== null &&
          (context.cadenceWeeks !== null || standaloneReschedule)),
    'coaching context requires seat type and cadence',
  );
  failUnless(
    context.intent !== 'book' || context.rescheduleTarget === null,
    'booking context requires null reschedule target',
  );
  failUnless(
    context.intent !== 'reschedule' || context.rescheduleTarget !== null,
    'rescheduling context requires a target',
  );
  failUnless(
    context.intent !== 'reschedule' || context.appointmentCount === null,
    'reschedule context requires null appointment count',
  );
};

const validateRescheduleTarget = (context: SchedulingContext): void => {
  const target = context.rescheduleTarget;
  if (target === null) return;

  validateScopes(target.allowedScopes);
  failUnless(
    context.kind !== 'chemistry' ||
      (target.seriesId === null &&
        (target.allowedScopes.length === 0 ||
          (target.allowedScopes.length === 1 &&
            target.allowedScopes[0] === 'appointment'))),
    'chemistry target must be standalone and appointment-scoped',
  );
  failUnless(
    context.kind !== 'chemistry' ||
      !context.eligibility.eligible ||
      (target.allowedScopes.length === 1 &&
        target.allowedScopes[0] === 'appointment'),
    'eligible chemistry target must allow appointment scope',
  );
  failUnless(
    context.kind !== 'coaching' ||
      context.intent !== 'reschedule' ||
      context.activeSeries === null ||
      target.seriesId === context.activeSeries.id,
    'active Series must identify the reschedule target Series',
  );
};

const validateSchedulingContext = (
  context:
    | AppointmentTypes.InternalSchedulingContext
    | AppointmentTypes.BrowserSchedulingContext
    | AppointmentTypes.AdminSchedulingContext
    | AppointmentTypes.ParticipantSchedulingContext,
): void => {
  validateEligibility(context.eligibility);
  validateSchedulingKindAndIntent(context);
  validateRescheduleTarget(context);
  failUnless(
    !('bookingPattern' in context) ||
      context.seatType !== null ||
      (!context.eligibility.eligible &&
        context.eligibility.reasons.includes('member_ineligible')),
    'v3 null seat type requires member-ineligible context',
  );
  failUnless(
    !('bookingPattern' in context) ||
      !context.eligibility.reasons.includes('active_series_exists'),
    'active Series cannot make a one-off booking context ineligible',
  );
  failUnless(
    context.activeSeries === null ||
      'bookingPattern' in context ||
      context.kind !== 'coaching' ||
      context.intent !== 'book' ||
      (!context.eligibility.eligible &&
        context.eligibility.reasons.includes('active_series_exists')),
    'active Series booking context must be ineligible',
  );

  validateAvailability(context);
};

const validateAdminSchedulingContext = (
  context: AppointmentTypes.AdminSchedulingContext,
): void => {
  failUnless(
    context.coach.personId === context.coachPersonId &&
      context.member.personId === context.memberPersonId,
    'admin scheduling identities must match requested person IDs',
  );
  failUnless(
    context.coach.displayName.trim().length > 0 &&
      context.coach.email.trim().length > 0 &&
      context.member.displayName.trim().length > 0 &&
      context.member.email.trim().length > 0,
    'admin scheduling identities must be complete',
  );
  failUnless(
    context.kind !== 'chemistry' || context.engagementEndDate === null,
    'chemistry context must not expose an engagement end date',
  );
  if (context.engagementEndDate !== null) {
    const parsed = new Date(`${context.engagementEndDate}T00:00:00Z`);
    failUnless(
      !Number.isNaN(parsed.valueOf()) &&
        parsed.toISOString().slice(0, 10) === context.engagementEndDate,
      'admin scheduling engagement end date must be an ISO date',
    );
  }
  failUnless(
    context.activeSeries === null ||
      parseTimestamp(context.activeSeries.affectedEndAt) >=
        parseTimestamp(context.activeSeries.nextSchedule.endAt),
    'admin active Series affected end must bound the actual tail',
  );
  validateSchedulingContext(context);
};

const ADMIN_APPOINTMENT_PAGE_ORDER_ASCENDING =
  'current_start_at_asc,appointment_id_asc';
const ADMIN_APPOINTMENT_PAGE_ORDER_DESCENDING =
  'current_start_at_desc,appointment_id_desc';

const validateAdminAppointmentPage = (
  page: AppointmentTypes.AdminAppointmentPage,
): void => {
  const order = page.order ?? ADMIN_APPOINTMENT_PAGE_ORDER_ASCENDING;
  page.appointments.forEach((appointment, index, appointments) => {
    if (index === 0) return;
    const previous = appointments[index - 1]!;
    const previousStart = parseTimestamp(previous.currentSchedule.startAt);
    const currentStart = parseTimestamp(appointment.currentSchedule.startAt);
    if (order === ADMIN_APPOINTMENT_PAGE_ORDER_DESCENDING) {
      failUnless(
        currentStart < previousStart ||
          (currentStart === previousStart && appointment.id < previous.id),
        'admin Appointments must be ordered by current start then id descending',
      );
      return;
    }
    failUnless(
      currentStart > previousStart ||
        (currentStart === previousStart && appointment.id > previous.id),
      'admin Appointments must be ordered by current start then id',
    );
  });
};

const validateChemistryCapacityPage = (
  page: AppointmentTypes.ChemistryCapacityPage,
): void => {
  page.coaches.forEach((coach, index, coaches) => {
    failUnless(
      index === 0 || coach.coachPersonId > coaches[index - 1]!.coachPersonId,
      'chemistry capacity must be uniquely ordered by coach person id',
    );
  });
};

const validateHistoryPage = (
  page: AppointmentTypes.AppointmentHistoryPage,
): void => {
  page.records.forEach((record, index, records) => {
    failUnless(
      record.appointmentId === page.appointmentId,
      'history record must identify the page Appointment',
    );
    if (index === 0) return;
    const previous = records[index - 1]!;
    failUnless(
      record.appointmentVersion > previous.appointmentVersion ||
        (record.appointmentVersion === previous.appointmentVersion &&
          record.id > previous.id),
      'history records must be ordered by Appointment version then record id',
    );
  });
};

const validateReminderPage = (
  page: AppointmentTypes.AppointmentReminderPage,
): void => {
  page.candidates.forEach((candidate, index, candidates) => {
    if (index === 0) return;
    const previous = candidates[index - 1]!;
    const previousStart = parseTimestamp(previous.currentSchedule.startAt);
    const currentStart = parseTimestamp(candidate.currentSchedule.startAt);
    failUnless(
      currentStart > previousStart ||
        (currentStart === previousStart &&
          candidate.appointmentId > previous.appointmentId),
      'reminder candidates must be ordered by current start then Appointment id',
    );
  });
};

const validateEndedAttendancePage = (
  page: AppointmentTypes.EndedAppointmentPage,
): void => {
  page.candidates.forEach((candidate, index, candidates) => {
    failUnless(
      candidate.kind === 'chemistry'
        ? candidate.seatType === null
        : candidate.seatType !== null,
      'ended attendance candidate has invalid seat type for its kind',
    );
    if (index === 0) return;
    const previous = candidates[index - 1]!;
    const previousEnd = parseTimestamp(previous.currentSchedule.endAt);
    const currentEnd = parseTimestamp(candidate.currentSchedule.endAt);
    failUnless(
      currentEnd > previousEnd ||
        (currentEnd === previousEnd &&
          candidate.appointmentId > previous.appointmentId),
      'ended attendance candidates must be ordered by current end then Appointment id',
    );
  });
};

const canonicalProtocolPolicy = mapKeys(
  JSON.parse(
    artifactTexts['./contracts/v1/fixtures/appointment-protocol-policy.json']!,
  ),
  toCamelCase,
) as AppointmentTypes.ProtocolPolicy;

const canonicalOperationMatrix = mapKeys(
  JSON.parse(
    artifactTexts['./contracts/v1/fixtures/appointment-operation-matrix.json']!,
  ),
  toCamelCase,
) as AppointmentTypes.OperationMatrix;

const canonicalJson = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(',')}]`;
  }
  if (value !== null && typeof value === 'object') {
    return `{${Object.entries(value)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${JSON.stringify(key)}:${canonicalJson(item)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
};

const validateProtocolPolicy = (
  policy: AppointmentTypes.ProtocolPolicy,
): void => {
  failUnless(
    canonicalJson(policy) === canonicalJson(canonicalProtocolPolicy),
    'protocol policy must exactly match the canonical policy',
  );
};

const validateAdminReviewQueuePage = (
  page: AppointmentTypes.AdminReviewQueuePage,
): void => {
  let previousDetectedAt = Number.NEGATIVE_INFINITY;
  let previousId = '';
  page.appointments.forEach((appointment) => {
    const reviewIssues = appointment.issues.filter(
      (issue) => issue.code === 'operator_review_required',
    );
    failUnless(
      reviewIssues.length === 1,
      'admin review Appointment must have exactly one operator review issue',
    );
    const detectedAt = parseTimestamp(reviewIssues[0]!.detectedAt);
    failUnless(
      detectedAt > previousDetectedAt ||
        (detectedAt === previousDetectedAt && appointment.id > previousId),
      'admin review Appointments must be ordered by issue detection then id',
    );
    previousDetectedAt = detectedAt;
    previousId = appointment.id;
  });
};

const validateBookCommand = (
  command:
    | AppointmentTypes.BookAppointmentCommand
    | AppointmentTypes.AdminBookAppointmentCommand,
): void => {
  failUnless(
    command.kind !== 'chemistry' ||
      (!('bookingPattern' in command) &&
        command.seatType === null &&
        command.recurrence === null),
    'chemistry booking requires null seat type and recurrence',
  );
  failUnless(
    command.kind !== 'coaching' ||
      (command.seatType !== null &&
        ('bookingPattern' in command
          ? !('recurrence' in command)
          : 'recurrence' in command && command.recurrence !== null)),
    'coaching booking requires seat type and recurrence',
  );
};

const validateTailCommand = (
  command:
    | AppointmentTypes.RescheduleAppointmentCommand
    | AppointmentTypes.CancelAppointmentCommand
    | AppointmentTypes.BrowserCoachCancelAppointmentCommandV2
    | AppointmentTypes.CoachCancelAppointmentCommandV2
    | AppointmentTypes.AdminRescheduleAppointmentCommand
    | AppointmentTypes.AdminCancelAppointmentCommand
    | AppointmentTypes.ParticipantMemberCancelAppointmentCommand
    | AppointmentTypes.ParticipantCoachCancelAppointmentCommand,
): void => {
  failUnless(
    command.expectedSeriesMutationVersion === null ||
      command.expectedSeriesMutationVersion > 0,
    'Series mutation version must be positive when present',
  );
  failUnless(
    command.scope !== 'this_and_following' ||
      command.expectedSeriesMutationVersion !== null,
    'tail command requires a Series mutation version',
  );
  if ('seriesId' in command) {
    failUnless(
      (command.seriesId === null &&
        command.expectedSeriesMutationVersion === null) ||
        (command.seriesId !== null &&
          command.expectedSeriesMutationVersion !== null),
      'Series ID and mutation version must describe the same membership',
    );
  }
};

const validateParticipantCancelCommand = (
  command:
    | AppointmentTypes.ParticipantMemberCancelAppointmentCommand
    | AppointmentTypes.ParticipantCoachCancelAppointmentCommand,
): void => {
  validateTailCommand(command);
  const hasStandaloneFence = (
    scope: AppointmentTypes.AppointmentScope,
    seriesId: string | null,
    expectedSeriesMutationVersion: number | null,
  ): boolean =>
    scope !== 'appointment' ||
    (seriesId === null && expectedSeriesMutationVersion === null);
  failUnless(
    hasStandaloneFence(
      command.scope,
      command.seriesId,
      command.expectedSeriesMutationVersion,
    ),
    'standalone participant cancellation requires a null Series fence',
  );
};

type ParticipantResult =
  | AppointmentTypes.ParticipantAppointmentMutationResult
  | AppointmentTypes.ParticipantAppointmentOperation;

const validatePendingParticipantResult = (result: ParticipantResult): void => {
  failUnless(
    result.writeOutcome === 'ambiguous' &&
      result.retryAfterSeconds !== null &&
      result.error === null &&
      result.reconciliationReason === null &&
      result.appointments.length === 0,
    'pending participant result requires retry timing without terminal fields',
  );
};

const validateConfirmedParticipantResult = (
  result: ParticipantResult,
): void => {
  failUnless(
    result.writeOutcome === 'applied' &&
      result.retryAfterSeconds === null &&
      result.error === null &&
      result.reconciliationReason === null &&
      result.appointments.length > 0 &&
      result.appointments.every(
        (appointment) => appointment.bookingStatus === 'confirmed',
      ),
    'confirmed participant result has contradictory Appointment fields',
  );
  if (!('command' in result)) return;
  failUnless(
    result.appointments.every(
      (appointment) =>
        appointment.lifecycleStatus ===
        (result.command === 'book' ? 'scheduled' : 'cancelled'),
    ),
    'confirmed participant operation has inconsistent Appointments',
  );
};

const validateRejectedParticipantResult = (result: ParticipantResult): void => {
  failUnless(
    result.writeOutcome === 'not_applied' &&
      result.retryAfterSeconds === null &&
      result.error?.writeOutcome === 'not_applied' &&
      result.reconciliationReason === null &&
      result.appointments.length === 0,
    'rejected participant result has contradictory fields',
  );
};

const validateReconciliationParticipantResult = (
  result: ParticipantResult,
): void => {
  failUnless(
    result.writeOutcome === 'ambiguous' &&
      result.retryAfterSeconds === null &&
      result.error === null &&
      result.reconciliationReason !== null &&
      result.reconciliationReason.trim().length > 0,
    'reconciliation participant result has contradictory fields',
  );
};

const validateParticipantResult = (result: ParticipantResult): void => {
  if ('appointmentIds' in result) {
    const snapshotIds = new Set(
      result.appointments.map((appointment) => appointment.id),
    );
    failUnless(
      snapshotIds.size === result.appointmentIds.length &&
        result.appointmentIds.every((id) => snapshotIds.has(id)),
      'participant operation Appointment IDs must match its snapshots',
    );
  }
  const validators = {
    confirmed: validateConfirmedParticipantResult,
    needs_reconciliation: validateReconciliationParticipantResult,
    pending: validatePendingParticipantResult,
    rejected: validateRejectedParticipantResult,
  } satisfies Record<
    AppointmentTypes.CoachCancelOperationStatus,
    (participantResult: ParticipantResult) => void
  >;
  validators[result.status](result);
};

const validateCoachCancelResult = (
  result:
    | AppointmentTypes.CoachCancelAppointmentMutationResultV2
    | AppointmentTypes.CoachCancelAppointmentOperationStatusV2,
): void => {
  failUnless(
    result.appointments.length > 0,
    'coach cancellation result requires authoritative Appointments',
  );
  result.appointments.forEach((appointment) => {
    const wire = mapKeys(appointment, toSnakeCase);
    const validateAppointment = compile('appointment.projection.browser.v1');
    failUnless(
      Boolean(validateAppointment(wire)),
      `coach cancellation result contains an invalid Appointment: ${ajv.errorsText(validateAppointment.errors)}`,
    );
  });
  if ('appointmentIds' in result) {
    const snapshotIds = new Set(
      result.appointments.map((appointment) => appointment.id),
    );
    failUnless(
      snapshotIds.size === result.appointmentIds.length &&
        result.appointmentIds.every((id) => snapshotIds.has(id)),
      'coach cancellation operation IDs must match authoritative Appointments',
    );
  }
  const terminalWithoutRetry = result.retryAfterSeconds === null;
  if (result.status === 'confirmed') {
    failUnless(
      result.rejection === null &&
        result.reconciliationReason === null &&
        terminalWithoutRetry &&
        result.appointments.every(
          (appointment) => appointment.lifecycleStatus === 'cancelled',
        ),
      'confirmed coach cancellation has invalid terminal fields',
    );
  } else if (result.status === 'pending') {
    failUnless(
      result.rejection === null &&
        result.reconciliationReason === null &&
        result.retryAfterSeconds !== null,
      'pending coach cancellation requires retry timing only',
    );
  } else if (result.status === 'rejected') {
    failUnless(
      result.rejection !== null &&
        result.reconciliationReason === null &&
        terminalWithoutRetry,
      'rejected coach cancellation requires rejection only',
    );
  } else {
    failUnless(
      result.rejection === null &&
        result.reconciliationReason !== null &&
        terminalWithoutRetry,
      'reconciliation-required coach cancellation requires a reason',
    );
  }
};

const validateBrowserCoachCancelCommand = (
  command: AppointmentTypes.BrowserCoachCancelAppointmentCommandV2,
): void => {
  validateTailCommand(command);
  failUnless(
    command.opsNote === null || command.opsNote.trim() === command.opsNote,
    'coach cancellation Ops note must be trimmed',
  );
};

const validateAdminSchedulingContextCommand = (
  command: AppointmentTypes.AdminSchedulingContextCommand,
): void => {
  failUnless(
    command.intent === 'reschedule'
      ? command.appointmentId !== undefined
      : command.appointmentId === undefined,
    command.intent === 'reschedule'
      ? 'admin rescheduling context requires Appointment ID'
      : 'admin booking context must omit Appointment ID',
  );
};

const validateReviewProof = (
  mode: 'dry_run' | 'apply',
  reviewToken: string | null,
  digest: string | null,
): void => {
  failUnless(
    mode !== 'dry_run' || (reviewToken === null && digest === null),
    'dry run requires null review proof',
  );
  failUnless(
    mode !== 'apply' ||
      (reviewToken !== null && reviewToken.length > 0 && digest !== null),
    'apply requires review token and digest',
  );
};

const validateAdoptCommand = (
  command: AppointmentTypes.AdoptAppointmentCommand,
): void => {
  failUnless(command.actor.role === 'admin', 'adopt actor must be admin');
  failUnless(
    command.kind !== 'chemistry' || command.seatType === null,
    'chemistry adopt requires null seat type',
  );
  failUnless(
    command.kind !== 'coaching' || command.seatType !== null,
    'coaching adopt requires seat type',
  );
  validateReviewProof(
    command.mode,
    command.reviewToken,
    command.canonicalRequestDigest,
  );
};

const validateRebindCommand = (
  command: AppointmentTypes.RebindAppointmentCommand,
): void => {
  failUnless(command.actor.role === 'admin', 'rebind actor must be admin');
  validateReviewProof(
    command.mode,
    command.reviewToken,
    command.canonicalRequestDigest,
  );
};

const expectedOperationMappings = [
  [
    'book',
    'rails_to_moments',
    '/rpc/v1/scheduling/appointments',
    'appointments:write',
    'appointment.command.book.v1',
    'appointment.mutation-result.browser.v1',
    'book',
    'fixtures/appointment-command-book.json',
    'POST',
    200,
    202,
    200,
  ],
  [
    'reschedule',
    'rails_to_moments',
    '/rpc/v1/scheduling/appointments/{id}/reschedule',
    'appointments:write',
    'appointment.command.reschedule.v1',
    'appointment.mutation-result.browser.v1',
    'reschedule',
    'fixtures/appointment-command-reschedule.json',
    'POST',
    200,
    202,
    200,
  ],
  [
    'cancel',
    'rails_to_moments',
    '/rpc/v1/scheduling/appointments/{id}/cancel',
    'appointments:write',
    'appointment.command.cancel.v1',
    'appointment.mutation-result.browser.v1',
    'cancel',
    'fixtures/appointment-command-cancel.json',
    'POST',
    200,
    202,
    200,
  ],
  [
    'admin_scheduling_context',
    'moments_admin_web',
    '/api/v1/admin/appointments/scheduling-context',
    'admin:coaches',
    'appointment.command.admin-scheduling-context.v1',
    'appointment.scheduling-context.admin.v1',
    'admin_scheduling_context',
    'fixtures/appointment-command-admin-scheduling-context.json',
    'GET',
    200,
    200,
    200,
  ],
  [
    'admin_book',
    'moments_admin_web',
    '/api/v1/admin/appointments',
    'admin:coaches',
    'appointment.command.admin-book.v1',
    'appointment.mutation-result.admin.v1',
    'admin_book',
    'fixtures/appointment-command-admin-book.json',
    'POST',
    200,
    202,
    200,
  ],
  [
    'admin_reschedule',
    'moments_admin_web',
    '/api/v1/admin/appointments/{id}/reschedule',
    'admin:coaches',
    'appointment.command.admin-reschedule.v1',
    'appointment.mutation-result.admin.v1',
    'admin_reschedule',
    'fixtures/appointment-command-admin-reschedule.json',
    'POST',
    200,
    202,
    200,
  ],
  [
    'admin_cancel',
    'moments_admin_web',
    '/api/v1/admin/appointments/{id}/cancel',
    'admin:coaches',
    'appointment.command.admin-cancel.v1',
    'appointment.mutation-result.admin.v1',
    'admin_cancel',
    'fixtures/appointment-command-admin-cancel.json',
    'POST',
    200,
    202,
    200,
  ],
  [
    'adopt',
    'moments_admin_web',
    '/api/v1/admin/appointments/adopt',
    'admin:coaches',
    'appointment.command.adopt.v1',
    'appointment.mutation-result.admin.v1',
    'adopt',
    'fixtures/appointment-command-adopt-apply.json',
    'POST',
    200,
    202,
    200,
  ],
  [
    'rebind',
    'moments_admin_web',
    '/api/v1/admin/appointments/{id}/rebind',
    'admin:coaches',
    'appointment.command.rebind.v1',
    'appointment.mutation-result.admin.v1',
    'rebind',
    'fixtures/appointment-command-rebind.json',
    'POST',
    200,
    202,
    200,
  ],
] as const;

const validateOperationMatrix = (
  matrix: AppointmentTypes.OperationMatrix,
): void => {
  failUnless(
    canonicalJson(matrix) === canonicalJson(canonicalOperationMatrix),
    'operation matrix must exactly match the canonical matrix',
  );
  matrix.operations.forEach((operation, index) => {
    const actual = [
      operation.command,
      operation.tier,
      operation.route,
      operation.scope,
      operation.requestCodec,
      operation.resultCodec,
      operation.adapterMethod,
      operation.callerFixture,
      operation.method,
      operation.confirmedHttpStatus,
      operation.pendingHttpStatus,
      operation.rejectedHttpStatus,
    ];
    failUnless(
      actual.every(
        (value, itemIndex) =>
          value === expectedOperationMappings[index]![itemIndex],
      ),
      `operation matrix entry ${index} does not match frozen mapping`,
    );
  });
};

interface ArtifactManifestEntry {
  path: string;
  sha256: string;
}

interface ArtifactManifest {
  artifacts: ArtifactManifestEntry[];
  contract_version: string;
  hash_algorithm: string;
  newline_policy: string;
}

const verifyArtifactText = (path: string, contents: string): void => {
  failUnless(
    contents.length > 0 &&
      contents.endsWith('\n') &&
      !contents.endsWith('\n\n') &&
      !contents.includes('\r'),
    `artifact ${path} must use LF and exactly one final LF`,
  );
};

const sha256 = async (contents: string): Promise<string> => {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(contents),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
};

export const verifyAppointmentContractArtifacts = async (): Promise<void> => {
  failUnless(
    artifactManifestText !== undefined,
    'appointment artifact manifest is missing',
  );
  verifyArtifactText('manifest.json', artifactManifestText);
  failUnless(
    (await sha256(artifactManifestText)) === CANONICAL_MANIFEST_SHA256,
    'appointment artifact manifest does not match the pinned canonical digest',
  );
  const parsed: unknown = JSON.parse(artifactManifestText);
  failUnless(
    parsed !== null && !Array.isArray(parsed) && typeof parsed === 'object',
    'appointment artifact manifest must be an object',
  );
  const manifestObject = parsed as Record<string, unknown>;
  failUnless(
    Object.keys(manifestObject).sort().join(',') ===
      'artifacts,contract_version,hash_algorithm,newline_policy',
    'appointment artifact manifest has unknown or missing fields',
  );
  const manifest = manifestObject as unknown as ArtifactManifest;
  failUnless(
    manifest.contract_version === 'appointment-artifacts.v1' &&
      manifest.hash_algorithm === 'sha256' &&
      manifest.newline_policy === 'exactly_one_lf' &&
      Array.isArray(manifest.artifacts),
    'appointment artifact manifest metadata is invalid',
  );

  const artifactEntries = Object.entries(artifactTexts)
    .map(
      ([modulePath, contents]) =>
        [modulePath.replace('./contracts/v1/', ''), contents] as const,
    )
    .sort(([left], [right]) => left.localeCompare(right));
  const actualPaths = artifactEntries.map(([path]) => path);
  const listedPaths = manifest.artifacts.map((artifact) => artifact.path);
  failUnless(
    listedPaths.every(
      (path, index) => index === 0 || path > listedPaths[index - 1]!,
    ) &&
      actualPaths.length === listedPaths.length &&
      actualPaths.every((path, index) => path === listedPaths[index]),
    'appointment artifact manifest paths must be complete and lexicographically ordered',
  );

  await Promise.all(
    artifactEntries.map(async ([path, contents], index) => {
      const manifestEntry = manifest.artifacts[index]!;
      failUnless(
        Object.keys(manifestEntry).sort().join(',') === 'path,sha256' &&
          /^[0-9a-f]{64}$/u.test(manifestEntry.sha256),
        `artifact ${path} has invalid manifest metadata`,
      );
      verifyArtifactText(path, contents);
      failUnless(
        (await sha256(contents)) === manifestEntry.sha256,
        `artifact ${path} SHA-256 mismatch`,
      );
    }),
  );
};

const createCodec = <TWire, TDomain, TDecoded extends TDomain = TDomain>(
  id: string,
  validate: ValidateFunction,
  validateSemantics?: SemanticValidator<TDomain>,
  decorateDecoded?: (value: TDomain) => TDecoded,
): AppointmentTypes.ContractCodec<TWire, TDecoded, TDomain> => ({
  decode(value: unknown) {
    if (!validate(value)) {
      throw new Error(`Invalid ${id}: ${ajv.errorsText(validate.errors)}`);
    }
    validateCapabilityNeutralWireSemantics(value);
    validateSeriesFenceWireSemantics(value);
    const domain = mapKeys(value, toCamelCase) as TDomain;
    validateSemantics?.(domain);
    return decorateDecoded ? decorateDecoded(domain) : (domain as TDecoded);
  },
  encode(value: TDomain) {
    validateSemantics?.(value);
    const wire = mapKeys(value, toSnakeCase);
    if (!validate(wire)) {
      throw new Error(`Invalid ${id}: ${ajv.errorsText(validate.errors)}`);
    }
    validateCapabilityNeutralWireSemantics(wire);
    validateSeriesFenceWireSemantics(wire);
    return wire as TWire;
  },
});

const markExactAppointment = (
  appointment: AppointmentTypes.BrowserAppointment,
): AppointmentTypes.ExactBrowserAppointment => {
  Object.defineProperty(appointment, APPOINTMENT_BROWSER_CONTRACT, {
    value: EXACT_APPOINTMENT_CONTRACT,
  });
  return appointment as AppointmentTypes.ExactBrowserAppointment;
};

const markExactAppointments = <
  TAppointment extends AppointmentTypes.BrowserAppointment,
  T extends { appointments: TAppointment[] },
>(
  value: T,
): Omit<T, 'appointments'> & {
  appointments: Array<TAppointment & AppointmentTypes.ExactBrowserAppointment>;
} => {
  value.appointments.forEach(markExactAppointment);
  return value as unknown as Omit<T, 'appointments'> & {
    appointments: Array<
      TAppointment & AppointmentTypes.ExactBrowserAppointment
    >;
  };
};

const markExactAppointmentV2 = (
  appointment: AppointmentTypes.BrowserAppointmentV2,
): AppointmentTypes.ExactBrowserAppointmentV2 => {
  Object.defineProperty(appointment, APPOINTMENT_BROWSER_CONTRACT, {
    value: NATIVE_COACH_RESCHEDULE_CONTRACT,
  });
  return appointment as AppointmentTypes.ExactBrowserAppointmentV2;
};

const markExactAppointmentsV2 = (
  value: AppointmentTypes.BrowserAppointmentPageV2,
): AppointmentTypes.ExactBrowserAppointmentPageV2 => {
  value.appointments.forEach(markExactAppointmentV2);
  return value as AppointmentTypes.ExactBrowserAppointmentPageV2;
};

export const appointmentContractV1 = {
  adminAppointment: createCodec<
    AppointmentTypes.AdminAppointmentWire,
    AppointmentTypes.AdminAppointment
  >(
    'appointment.projection.admin.v1',
    compile('appointment.projection.admin.v1'),
  ),
  adminAppointmentPage: createCodec<
    AppointmentTypes.AdminAppointmentPageWire,
    AppointmentTypes.AdminAppointmentPage
  >(
    'appointment.page.admin.v1',
    compile('appointment.page.admin.v1'),
    validateAdminAppointmentPage,
  ),
  adminMutationResult: createCodec<
    AppointmentTypes.AdminAppointmentMutationResultWire,
    AppointmentTypes.AdminAppointmentMutationResult
  >(
    'appointment.mutation-result.admin.v1',
    compile('appointment.mutation-result.admin.v1'),
    validateMutationResult,
  ),
  adminMutationReview: createCodec<
    AppointmentTypes.AdminMutationReviewWire,
    AppointmentTypes.AdminMutationReview
  >(
    'appointment.admin-mutation-review.v1',
    compile('appointment.admin-mutation-review.v1'),
  ),
  adminReviewQueuePage: createCodec<
    AppointmentTypes.AdminReviewQueuePageWire,
    AppointmentTypes.AdminReviewQueuePage
  >(
    'appointment.admin-review-queue.v1',
    compile('appointment.admin-review-queue.v1'),
    validateAdminReviewQueuePage,
  ),
  adminSchedulingContext: createCodec<
    AppointmentTypes.AdminSchedulingContextWire,
    AppointmentTypes.AdminSchedulingContext
  >(
    'appointment.scheduling-context.admin.v1',
    compile('appointment.scheduling-context.admin.v1'),
    validateAdminSchedulingContext,
  ),
  adminSchedulingContextCommand: createCodec<
    AppointmentTypes.AdminSchedulingContextCommandWire,
    AppointmentTypes.AdminSchedulingContextCommand
  >(
    'appointment.command.admin-scheduling-context.v1',
    compile('appointment.command.admin-scheduling-context.v1'),
    validateAdminSchedulingContextCommand,
  ),
  adminBookCommand: createCodec<
    AppointmentTypes.AdminBookAppointmentCommandWire,
    AppointmentTypes.AdminBookAppointmentCommand
  >(
    'appointment.command.admin-book.v1',
    compile('appointment.command.admin-book.v1'),
    validateBookCommand,
  ),
  adminRescheduleCommand: createCodec<
    AppointmentTypes.AdminRescheduleAppointmentCommandWire,
    AppointmentTypes.AdminRescheduleAppointmentCommand
  >(
    'appointment.command.admin-reschedule.v1',
    compile('appointment.command.admin-reschedule.v1'),
    validateTailCommand,
  ),
  adminCancelCommand: createCodec<
    AppointmentTypes.AdminCancelAppointmentCommandWire,
    AppointmentTypes.AdminCancelAppointmentCommand
  >(
    'appointment.command.admin-cancel.v1',
    compile('appointment.command.admin-cancel.v1'),
    validateTailCommand,
  ),
  adoptCommand: createCodec<
    AppointmentTypes.AdoptAppointmentCommandWire,
    AppointmentTypes.AdoptAppointmentCommand
  >(
    'appointment.command.adopt.v1',
    compile('appointment.command.adopt.v1'),
    validateAdoptCommand,
  ),
  adminCancellationEvidencePage: createCodec<
    AppointmentTypes.AdminCancellationEvidencePageWire,
    AppointmentTypes.AdminCancellationEvidencePage
  >(
    'appointment.cancellation-evidence-page.admin.v1',
    compile('appointment.cancellation-evidence-page.admin.v1'),
  ),
  bookCommand: createCodec<
    AppointmentTypes.BookAppointmentCommandV1Wire,
    AppointmentTypes.BookAppointmentCommandV1
  >(
    'appointment.command.book.v1',
    compile('appointment.command.book.v1'),
    validateBookCommand,
  ),
  browserAppointment: createCodec<
    AppointmentTypes.BrowserAppointmentWire,
    AppointmentTypes.BrowserAppointment,
    AppointmentTypes.ExactBrowserAppointment
  >(
    'appointment.projection.browser.v1',
    compile('appointment.projection.browser.v1'),
    undefined,
    markExactAppointment,
  ),
  browserAppointmentPage: createCodec<
    AppointmentTypes.BrowserAppointmentPageWire,
    AppointmentTypes.BrowserAppointmentPage,
    AppointmentTypes.ExactBrowserAppointmentPage
  >(
    'appointment.page.browser.v1',
    compile('appointment.page.browser.v1'),
    validateBrowserAppointmentPage,
    markExactAppointments,
  ),
  browserMutationResult: createCodec<
    AppointmentTypes.AppointmentMutationResultWire,
    AppointmentTypes.AppointmentMutationResult,
    AppointmentTypes.ExactAppointmentMutationResult
  >(
    'appointment.mutation-result.browser.v1',
    compile('appointment.mutation-result.browser.v1'),
    validateMutationResult,
    markExactAppointments,
  ),
  browserSchedulingContext: createCodec<
    AppointmentTypes.BrowserSchedulingContextV1Wire,
    AppointmentTypes.BrowserSchedulingContextV1
  >(
    'appointment.scheduling-context.browser.v1',
    compile('appointment.scheduling-context.browser.v1'),
    validateSchedulingContext,
  ),
  cancelCommand: createCodec<
    AppointmentTypes.CancelAppointmentCommandWire,
    AppointmentTypes.CancelAppointmentCommand
  >(
    'appointment.command.cancel.v1',
    compile('appointment.command.cancel.v1'),
    validateTailCommand,
  ),
  chemistryCapacityPage: createCodec<
    AppointmentTypes.ChemistryCapacityPageWire,
    AppointmentTypes.ChemistryCapacityPage
  >(
    'appointment.capacity-page.v1',
    compile('appointment.capacity-page.v1'),
    validateChemistryCapacityPage,
  ),
  endedAttendancePage: createCodec<
    AppointmentTypes.EndedAppointmentPageWire,
    AppointmentTypes.EndedAppointmentPage
  >(
    'appointment.ended-attendance-page.v1',
    compile('appointment.ended-attendance-page.v1'),
    validateEndedAttendancePage,
  ),
  historyPage: createCodec<
    AppointmentTypes.AppointmentHistoryPageWire,
    AppointmentTypes.AppointmentHistoryPage
  >(
    'appointment.history-page.v1',
    compile('appointment.history-page.v1'),
    validateHistoryPage,
  ),
  internalSchedulingContext: createCodec<
    AppointmentTypes.InternalSchedulingContextV1Wire,
    AppointmentTypes.InternalSchedulingContextV1
  >(
    'appointment.scheduling-context.internal.v1',
    compile('appointment.scheduling-context.internal.v1'),
    validateSchedulingContext,
  ),
  operationMatrix: createCodec<
    AppointmentTypes.OperationMatrixWire,
    AppointmentTypes.OperationMatrix
  >(
    'appointment.operation-matrix.v1',
    compile('appointment.operation-matrix.v1'),
    validateOperationMatrix,
  ),
  operationStatus: createCodec<
    AppointmentTypes.AppointmentOperationStatusWire,
    AppointmentTypes.AppointmentOperationStatus
  >(
    'appointment.operation-status.v1',
    compile('appointment.operation-status.v1'),
    validateOperationStatus,
  ),
  participantAppointment: createCodec<
    AppointmentTypes.Wire<AppointmentTypes.ParticipantAppointment>,
    AppointmentTypes.ParticipantAppointment
  >(
    'appointment.participant-appointment.v1',
    compile('appointment.participant-appointment.v1'),
  ),
  participantAppointmentDetail: createCodec<
    AppointmentTypes.Wire<AppointmentTypes.ParticipantAppointmentDetail>,
    AppointmentTypes.ParticipantAppointmentDetail
  >(
    'appointment.participant-appointment-detail.v1',
    compile('appointment.participant-appointment-detail.v1'),
  ),
  participantBookCommand: createCodec<
    AppointmentTypes.Wire<AppointmentTypes.ParticipantBookAppointmentCommand>,
    AppointmentTypes.ParticipantBookAppointmentCommand
  >(
    'appointment.participant-book.v1',
    compile('appointment.participant-book.v1'),
  ),
  participantCoachCancelCommand: createCodec<
    AppointmentTypes.Wire<AppointmentTypes.ParticipantCoachCancelAppointmentCommand>,
    AppointmentTypes.ParticipantCoachCancelAppointmentCommand
  >(
    'appointment.participant-coach-cancel.v1',
    compile('appointment.participant-coach-cancel.v1'),
    validateParticipantCancelCommand,
  ),
  participantError: createCodec<
    AppointmentTypes.Wire<ParticipantAppointmentErrorEnvelope>,
    ParticipantAppointmentErrorEnvelope
  >(
    'appointment.participant-error.v1',
    compile('appointment.participant-error.v1'),
  ),
  participantMemberCancelCommand: createCodec<
    AppointmentTypes.Wire<AppointmentTypes.ParticipantMemberCancelAppointmentCommand>,
    AppointmentTypes.ParticipantMemberCancelAppointmentCommand
  >(
    'appointment.participant-member-cancel.v1',
    compile('appointment.participant-member-cancel.v1'),
    validateParticipantCancelCommand,
  ),
  participantMutationResult: createCodec<
    AppointmentTypes.Wire<AppointmentTypes.ParticipantAppointmentMutationResult>,
    AppointmentTypes.ParticipantAppointmentMutationResult
  >(
    'appointment.participant-mutation-result.v1',
    compile('appointment.participant-mutation-result.v1'),
    validateParticipantResult,
  ),
  participantOperation: createCodec<
    AppointmentTypes.Wire<AppointmentTypes.ParticipantAppointmentOperation>,
    AppointmentTypes.ParticipantAppointmentOperation
  >(
    'appointment.participant-operation.v1',
    compile('appointment.participant-operation.v1'),
    validateParticipantResult,
  ),
  participantSchedulingContext: createCodec<
    AppointmentTypes.Wire<AppointmentTypes.ParticipantSchedulingContext>,
    AppointmentTypes.ParticipantSchedulingContext
  >(
    'appointment.participant-scheduling-context.v1',
    compile('appointment.participant-scheduling-context.v1'),
    validateSchedulingContext,
  ),
  protocolPolicy: createCodec<
    AppointmentTypes.ProtocolPolicyWire,
    AppointmentTypes.ProtocolPolicy
  >(
    'appointment.protocol-policy.v1',
    compile('appointment.protocol-policy.v1'),
    validateProtocolPolicy,
  ),
  rebindCommand: createCodec<
    AppointmentTypes.RebindAppointmentCommandWire,
    AppointmentTypes.RebindAppointmentCommand
  >(
    'appointment.command.rebind.v1',
    compile('appointment.command.rebind.v1'),
    validateRebindCommand,
  ),
  reminderPage: createCodec<
    AppointmentTypes.AppointmentReminderPageWire,
    AppointmentTypes.AppointmentReminderPage
  >(
    'appointment.reminder-page.v1',
    compile('appointment.reminder-page.v1'),
    validateReminderPage,
  ),
  rescheduleCommand: createCodec<
    AppointmentTypes.RescheduleAppointmentCommandWire,
    AppointmentTypes.RescheduleAppointmentCommand
  >(
    'appointment.command.reschedule.v1',
    compile('appointment.command.reschedule.v1'),
    validateTailCommand,
  ),
};

export const appointmentContractV2 = {
  adminRescheduleOperationPage: createCodec<
    AppointmentTypes.AdminRescheduleOperationPageWire,
    AppointmentTypes.AdminRescheduleOperationPage
  >(
    'appointment.reschedule-operation-page.admin.v2',
    compile('appointment.reschedule-operation-page.admin.v2'),
  ),
  bookCommand: createCodec<
    AppointmentTypes.BookAppointmentCommandV2Wire,
    AppointmentTypes.BookAppointmentCommandV2
  >(
    'appointment.command.book.v2',
    compile('appointment.command.book.v2'),
    validateBookCommand,
  ),
  browserAppointment: createCodec<
    AppointmentTypes.BrowserAppointmentV2Wire,
    AppointmentTypes.BrowserAppointmentV2,
    AppointmentTypes.ExactBrowserAppointmentV2
  >(
    'appointment.projection.browser.v2',
    compile('appointment.projection.browser.v2'),
    undefined,
    markExactAppointmentV2,
  ),
  browserAppointmentPage: createCodec<
    AppointmentTypes.BrowserAppointmentPageV2Wire,
    AppointmentTypes.BrowserAppointmentPageV2,
    AppointmentTypes.ExactBrowserAppointmentPageV2
  >(
    'appointment.page.browser.v2',
    compile('appointment.page.browser.v2'),
    validateBrowserAppointmentPage,
    markExactAppointmentsV2,
  ),
  browserCoachCancelCommand: createCodec<
    AppointmentTypes.BrowserCoachCancelAppointmentCommandV2Wire,
    AppointmentTypes.BrowserCoachCancelAppointmentCommandV2
  >(
    'appointment.command.coach-cancel.browser.v2',
    compile('appointment.command.coach-cancel.browser.v2'),
    validateBrowserCoachCancelCommand,
  ),
  browserMutationResult: createCodec<
    AppointmentTypes.CoachCancelAppointmentMutationResultV2Wire,
    AppointmentTypes.CoachCancelAppointmentMutationResultV2,
    AppointmentTypes.DecodedCoachCancelAppointmentMutationResultV2
  >(
    'appointment.mutation-result.browser.v2',
    compile('appointment.mutation-result.browser.v2'),
    validateCoachCancelResult,
    markExactAppointments,
  ),
  coachCancelCommand: createCodec<
    AppointmentTypes.CoachCancelAppointmentCommandV2Wire,
    AppointmentTypes.CoachCancelAppointmentCommandV2
  >(
    'appointment.command.coach-cancel.v2',
    compile('appointment.command.coach-cancel.v2'),
    validateTailCommand,
  ),
  browserSchedulingContext: createCodec<
    AppointmentTypes.BrowserSchedulingContextV2Wire,
    AppointmentTypes.BrowserSchedulingContextV2
  >(
    'appointment.scheduling-context.browser.v2',
    compile('appointment.scheduling-context.browser.v2'),
    validateSchedulingContext,
  ),
  browserRescheduleCommand: createCodec<
    AppointmentTypes.CoachAppointmentRescheduleRequestWire,
    Omit<
      AppointmentTypes.CoachAppointmentRescheduleRequest,
      'appointmentId'
    > & {
      contractVersion: 'appointment-browser-reschedule.v2';
    }
  >(
    'appointment.command.browser-reschedule.v2',
    compile('appointment.command.browser-reschedule.v2'),
  ),
  browserRescheduleOperation: createCodec<
    AppointmentTypes.CoachAppointmentRescheduleOperationWire,
    AppointmentTypes.CoachAppointmentRescheduleOperation
  >(
    'appointment.operation-status.v2',
    compile('appointment.operation-status.v2'),
  ),
  browserRescheduleResult: createCodec<
    AppointmentTypes.CoachAppointmentRescheduleResultWire,
    AppointmentTypes.CoachAppointmentRescheduleResult
  >(
    'appointment.reschedule-result.browser.v2',
    compile('appointment.reschedule-result.browser.v2'),
  ),
  browserRescheduleReview: createCodec<
    AppointmentTypes.CoachAppointmentRescheduleReviewWire,
    AppointmentTypes.CoachAppointmentRescheduleReview
  >(
    'appointment.reschedule-review.browser.v2',
    compile('appointment.reschedule-review.browser.v2'),
  ),
  browserRescheduleReviewCommand: createCodec<
    AppointmentTypes.CoachAppointmentRescheduleReviewRequestWire,
    Omit<
      AppointmentTypes.CoachAppointmentRescheduleReviewRequest,
      'appointmentId'
    > & {
      contractVersion: 'appointment-browser-reschedule-review.v2';
    }
  >(
    'appointment.command.browser-reschedule-review.v2',
    compile('appointment.command.browser-reschedule-review.v2'),
  ),
  browserRescheduleSchedulingContext: createCodec<
    AppointmentTypes.BrowserRescheduleSchedulingContextWire,
    AppointmentTypes.BrowserRescheduleSchedulingContext
  >(
    'appointment.scheduling-context.reschedule.browser.v2',
    compile('appointment.scheduling-context.reschedule.browser.v2'),
  ),
  internalSchedulingContext: createCodec<
    AppointmentTypes.InternalSchedulingContextV2Wire,
    AppointmentTypes.InternalSchedulingContextV2
  >(
    'appointment.scheduling-context.internal.v2',
    compile('appointment.scheduling-context.internal.v2'),
    validateSchedulingContext,
  ),
  operationStatus: createCodec<
    AppointmentTypes.CoachCancelAppointmentOperationStatusV2Wire,
    AppointmentTypes.CoachCancelAppointmentOperationStatusV2,
    AppointmentTypes.DecodedCoachCancelAppointmentOperationStatusV2
  >(
    'appointment.operation-status.v2',
    compile('appointment.operation-status.v2'),
    validateCoachCancelResult,
    markExactAppointments,
  ),
};

export const appointmentContractV3 = {
  bookCommand: createCodec<
    AppointmentTypes.BookAppointmentCommandV3Wire,
    AppointmentTypes.BookAppointmentCommandV3
  >(
    'appointment.command.book.v3',
    compile('appointment.command.book.v3'),
    validateBookCommand,
  ),
  browserSchedulingContext: createCodec<
    AppointmentTypes.BrowserSchedulingContextV3Wire,
    AppointmentTypes.BrowserSchedulingContextV3
  >(
    'appointment.scheduling-context.browser.v3',
    compile('appointment.scheduling-context.browser.v3'),
    validateSchedulingContext,
  ),
};
